<template>
  <div class="max-w-7xl mx-auto px-4 py-8 font-inter">
    <!-- Skeleton State -->
    <div v-if="isPageLoading" class="space-y-8 animate-pulse">
      <div>
        <div class="mb-6">
          <div class="h-4 w-32 bg-gray-200 rounded"></div>
        </div>
        <div class="flex flex-col gap-4">
          <div class="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
            <div class="space-y-3">
              <div class="h-7 w-56 bg-gray-200 rounded"></div>
              <div class="flex gap-2 flex-wrap">
                <div class="h-4 w-24 bg-gray-200 rounded"></div>
                <div class="h-4 w-28 bg-gray-200 rounded"></div>
                <div class="h-4 w-32 bg-gray-200 rounded"></div>
              </div>
            </div>
            <div class="h-9 w-36 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
        <div v-for="n in 5" :key="n" class="bg-white border border-gray-100 rounded-xl shadow-sm p-4">
          <div class="h-4 w-24 bg-gray-200 rounded mb-3"></div>
          <div class="h-7 w-20 bg-gray-200 rounded"></div>
        </div>
      </div>

      <div class="bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-4">
        <div class="h-5 w-48 bg-gray-200 rounded"></div>
        <div class="h-4 w-full bg-gray-200 rounded"></div>
        <div class="h-4 w-3/4 bg-gray-200 rounded"></div>
      </div>

      <div class="bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-6">
        <div class="flex flex-wrap gap-4">
          <div class="h-10 w-40 bg-gray-200 rounded"></div>
          <div class="h-10 w-40 bg-gray-200 rounded"></div>
          <div class="h-10 w-40 bg-gray-200 rounded"></div>
          <div class="h-10 w-40 bg-gray-200 rounded"></div>
        </div>
        <div class="grid grid-cols-1 xl:grid-cols-2 gap-5">
          <div class="space-y-4">
            <div v-for="n in 3" :key="n" class="h-36 bg-gray-100 border border-gray-200 rounded-xl"></div>
          </div>
          <div class="h-[360px] xl:h-[520px] bg-gray-100 border border-gray-200 rounded-xl"></div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div v-else-if="activePool" class="space-y-8">
      <!-- Header Section -->
      <div>
        <div class="mb-6">
          <button @click="goBackToList" class="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900">
            <ArrowLeft class="h-4 w-4" />
            Back to Talent Pools
          </button>
        </div>

        <div class="flex flex-col">
          <div class="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
            <div>
              <div class="flex items-center gap-3 mb-2">
                <h1 class="text-3xl font-bold text-gray-900 leading-tight">{{ activePool.title }}</h1>
                <span class="text-sm font-semibold px-2.5 py-0.5 rounded-full hidden" :class="talentPoolStatus.classes">
                  {{ talentPoolStatus.text }}
                </span>
              </div>
              <div class="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
                <div class="flex items-center gap-1.5">
                  <Briefcase class="h-4 w-4 text-teal-600" />
                  <span>{{ displayWorkType }}</span>
                </div>
                <div class="flex items-center gap-1.5">
                  <MapPin class="h-4 w-4 text-teal-600" />
                  <span>{{ activePool.location }}</span>
                </div>
                <div v-if="activePool.hiring_timeline || activePool.timeline_date || activePool.timeline" class="flex items-center gap-1.5">
                  <Calendar class="h-4 w-4 text-teal-600" />
                  <span>{{ formattedHiringTimeline }}</span>
                </div>
                <div class="flex items-center gap-1.5">
                  <Calendar class="h-4 w-4 text-teal-600" />
                  <span>Created {{ formattedCreationDate }}</span>
                </div>
              </div>
            </div>
            <button @click="editModal?.show()" class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 flex items-center gap-2 transition-colors">
              <Settings class="h-4 w-4" />
              <span>Edit Talent Pool</span>
            </button>
          </div>
        </div>
      </div>
      <!-- Stats Cards -->
      <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
        <div
          v-for="stat in statCards"
          :key="stat.label"
          class="bg-white border border-gray-100 rounded-xl shadow-sm p-4 transition hover:-translate-y-0.5 hover:shadow-md"
        >
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-[10px] uppercase tracking-[0.12em] text-gray-500 font-semibold">{{ stat.label }}</p>
              <p class="text-2xl font-semibold text-slate-900 leading-none mt-1">{{ stat.value }}</p>
              <p v-if="stat.subtext" class="text-[11px] text-gray-500 mt-1 leading-snug">{{ stat.subtext }}</p>
            </div>
            <span :class="['h-9 w-9 rounded-full flex items-center justify-center ring-2 ring-opacity-50', stat.iconRing, stat.iconBg]">
              <component :is="stat.icon" class="h-4 w-4" :class="stat.iconColor" />
            </span>
          </div>
        </div>
      </div>

      <!-- Description, Requirements, etc. -->
      <div class="space-y-8">
        <!-- Description -->
        <div class="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-3">About this Talent Pool</h3>
          <p class="text-gray-700 prose prose-sm max-w-none leading-relaxed" v-html="activePool.description_html"></p>
        </div>

        <!-- Requirements / Qualifications Row -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div class="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
            <h3 class="text-lg font-semibold text-gray-900 mb-3">Requirements</h3>
            <div v-if="activePool.requirements && activePool.requirements.length > 0" class="flex flex-wrap gap-2">
              <span v-for="req in activePool.requirements" :key="req" class="bg-teal-600 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
                {{ req }}
              </span>
            </div>
            <p v-else class="text-sm text-gray-500 italic">No specific requirements listed.</p>
          </div>

          <div class="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
            <h3 class="text-lg font-semibold text-gray-900 mb-3">Qualifications</h3>
            <div v-if="activePool.qualifications && activePool.qualifications.length > 0" class="flex flex-wrap gap-2">
              <span v-for="qual in activePool.qualifications" :key="qual" class="bg-teal-600 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
                {{ qual }}
              </span>
            </div>
            <p v-else class="text-sm text-gray-500 italic">No specific qualifications listed.</p>
          </div>
        </div>

        <!-- Project Details (Conditional) -->
        <div v-if="activePool.project" class="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-3">Project Details</h3>
          <div class="space-y-4 text-sm">
            <h4 class="font-bold text-gray-800">{{ activePool.project.name }}</h4>
            <p class="text-gray-600">{{ activePool.project.summary }}</p>
            <div class="grid grid-cols-3 gap-4 pt-2">
              <div>
                <span class="text-xs text-gray-500 block">Budget</span>
                <span class="font-medium text-gray-700">${{ Number(activePool.project.budget).toLocaleString() }}</span>
              </div>
              <div>
                <span class="text-xs text-gray-500 block">Start Date</span>
                <span class="font-medium text-gray-700">{{ activePool.project.start_date }}</span>
              </div>
              <div>
                <span class="text-xs text-gray-500 block">Expected Length</span>
                <span class="font-medium text-gray-700">{{ activePool.project.length }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Talent Pool Members Section -->
      <div class="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div class="p-6">
          <div class="flex flex-col gap-3">
            <h3 class="text-lg font-semibold text-gray-900">Talent Pool Members ({{ filteredContractors.length }})</h3>
            <div class="w-full rounded-xl border border-gray-200 bg-gray-50/60 p-4 shadow-sm">
              <div class="flex flex-col lg:flex-row lg:items-end lg:gap-4 gap-3">
                <div class="flex-1 min-w-[200px]">
                  <label class="block text-xs font-semibold text-gray-500 mb-1">Search</label>
                  <div class="relative">
                    <Search class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <input
                      v-model="searchQuery"
                      placeholder="Search members..."
                      class="w-full rounded-lg border border-gray-200 bg-white pl-9 pr-3 py-2 text-sm text-gray-800 focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
                    />
                  </div>
                </div>
                <div class="flex items-end gap-2">
                  <div class="flex flex-col">
                    <label class="block text-xs font-semibold text-gray-500 mb-1">Min rate</label>
                    <input
                      v-model="filters.minRate"
                      type="number"
                      min="0"
                      step="1"
                      placeholder="0"
                      class="w-24 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
                    />
                  </div>
                  <span class="text-sm text-gray-400 pb-2">-</span>
                  <div class="flex flex-col">
                    <label class="block text-xs font-semibold text-gray-500 mb-1">Max rate</label>
                    <input
                      v-model="filters.maxRate"
                      type="number"
                      min="0"
                      step="1"
                      placeholder="Any"
                      class="w-24 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
                    />
                  </div>
                </div>
                <div class="w-full lg:w-48">
                  <label class="block text-xs font-semibold text-gray-500 mb-1">Availability</label>
                  <select
                    v-model="filters.availability"
                    class="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
                  >
                    <option value="">Any availability</option>
                    <option value="immediate">Available now</option>
                    <option value="one-week">Available within 1 week</option>
                    <option value="two-weeks">Available within 2 weeks</option>
                    <option value="one-month">Available within 1 month</option>
                    <option value="not-specified">Availability not specified</option>
                  </select>
                </div>
                <div class="w-full lg:w-48">
                  <label class="block text-xs font-semibold text-gray-500 mb-1">Joined</label>
                  <select
                    v-model="filters.joinedRange"
                    class="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
                  >
                    <option value="">Any join date</option>
                    <option value="this-week">Joined this week</option>
                    <option value="two-weeks">Joined last 2 weeks</option>
                    <option value="one-month">Joined last month</option>
                    <option value="three-months">Joined last 3 months</option>
                    <option value="older">Joined > 3 months ago</option>
                  </select>
                </div>
                <div class="flex items-end">
                  <button
                    @click="resetFilters"
                    class="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                  >
                    Clear filters
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="border-t border-gray-200 p-6 pt-4">
          <!-- Contractors Grid -->
          <div v-if="isMembersLoading" class="text-center py-6 text-gray-500 text-sm">Loading members...</div>

          <div v-else-if="filteredContractors.length > 0" class="space-y-4">
            <div class="grid grid-cols-1 xl:grid-cols-2 gap-5">
              <div :class="['space-y-4', inlineMapHeightClass, 'overflow-y-auto pr-1']">
                <ContractorCard
                  v-for="contractor in filteredContractors"
                  :key="getContractorId(contractor)"
                  :id="`contractor-card-${getContractorId(contractor)}`"
                  :contractor="contractor"
                  :is-selected="getContractorId(contractor) === selectedContractorId"
                  @view-profile="handleViewProfile"
                  @message="handleMessage"
                  @download-resume="handleDownloadResume"
                  @select="handleSelectContractor"
                />
              </div>
              <div class="w-full">
                <ContractorMap
                  :contractors="contractorsWithCoords"
                  :selected-id="selectedContractorId"
                  :pool-location="poolLocation"
                  :map-height-class="inlineMapHeightClass"
                  @marker-click="handleMarkerClick"
                />
              </div>
            </div>
          </div>


          <!-- Empty state -->
          <div v-else class="text-center py-12">
            <p class="text-gray-500 font-medium">No members found</p>
            <p class="text-gray-400 text-sm mt-1">Try adjusting your search query.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Not Found State -->
    <div v-else class="text-center py-16">
      <p class="text-lg text-gray-600">Talent Pool not found.</p>
    </div>
  </div>

  <EditModal ref="editModal" />
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useTalentPoolsStore } from '@/stores/talentPoolsStore';
import { formatDate, formatHiringTimeline } from '@/utils/formatters';
import { ArrowLeft, Briefcase, MapPin, Calendar, Settings, Users, CheckCircle, Search, Award, Timer, Map } from 'lucide-vue-next';
import { EditModal } from '@/components/marketplace/talentPools'
import ContractorCard from '@/components/marketplace/talentPools/ContractorCard.vue';
import ContractorMap from '@/components/marketplace/talentPools/ContractorMap.vue';
import genericService from '@/services/marketplace/generic.service';

const route = useRoute();
const router = useRouter();
const talentPoolsStore = useTalentPoolsStore();
const editModal = ref(null)

// --- STATE ---
const searchQuery = ref('');
const filters = reactive({
  minRate: '',
  maxRate: '',
  availability: '',
  joinedRange: '',
});
const isBooting = ref(true);
const selectedContractorId = ref(null);

// --- HELPERS ---
const availabilityToDays = (availabilityText) => {
  const text = (availabilityText || '').toLowerCase();
  if (text.includes('immediate')) return 0;
  if (text.includes('one week')) return 7;
  if (text.includes('two weeks')) return 14;
  if (text.includes('one month')) return 30;
  if (text.startsWith('from')) {
    const datePart = text.replace('from', '').trim();
    const match = datePart.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    const parsed = match ? new Date(+match[3], +match[2] - 1, +match[1]) : new Date(datePart);
    if (!isNaN(parsed.getTime())) {
      const diffMs = parsed - new Date();
      return diffMs / (1000 * 60 * 60 * 24);
    }
  }
  return null;
};

const inlineMapHeightClass = 'h-[360px] xl:h-[520px]';

const getContractorId = (contractor) =>
  contractor?._computedId ||
  contractor?.id ||
  contractor?.contractorId ||
  contractor?.contractor_id ||
  contractor?.email ||
  contractor?.full_name;

const getContractorPhoto = (contractor) =>
  contractor?.photo ||
  genericService.getProfilePictureLink(
    contractor?.photo_hash,
    contractor?.profile_picture_s3_key
  );

const parseCoordinate = (value) => {
  const parsed = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(parsed) ? parsed : null;
};

const contractorHasCoordinates = (contractor) => {
  const lat = parseCoordinate(contractor?.location_lat);
  const lng = parseCoordinate(contractor?.location_lng);
  return lat !== null && lng !== null;
};

const poolLocation = computed(() => {
  const lat = parseCoordinate(activePool.value?.location_lat);
  const lng = parseCoordinate(activePool.value?.location_lng);
  if (lat === null || lng === null) return null;
  return { lat, lng, title: activePool.value?.title };
});

const isPageLoading = computed(() =>
  isBooting.value || talentPoolsStore.isLoadingActivePool || (!activePool.value && isMembersLoading.value)
);

// --- DATA FETCHING ---
const poolId = route.params.id;
onMounted(() => {
  talentPoolsStore.fetchTalentPoolById(poolId, "company");
  talentPoolsStore.getMembers(poolId);
  isBooting.value = false;
});

// --- COMPUTED PROPERTIES ---
const activePool = computed(() => talentPoolsStore.activePool);
const isMembersLoading = computed(() => talentPoolsStore.isLoadingMembers);

const formattedCreationDate = computed(() => {
  return activePool.value ? formatDate(activePool.value.creation_date) : '';
});

const displayWorkType = computed(() => {
  const value = activePool.value?.work_type;
  if (!value) return 'Not specified';
  const text = String(value).replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
});

const formattedHiringTimeline = computed(() =>
  formatHiringTimeline(
    activePool.value?.hiring_timeline,
    activePool.value?.timeline_date,
    activePool.value?.timeline
  )
);

const formatMonths = (months) => {
  if (months === undefined || months === null) return '—';
  const rounded = Math.round(months);
  if (rounded < 12) return `${rounded} mo`;
  const years = Math.floor(rounded / 12);
  const remainingMonths = rounded % 12;
  return remainingMonths ? `${years} yr ${remainingMonths} mo` : `${years} yr`;
};

const membersCount = computed(() => {
  const apiCount = activePool.value?.members_count;
  return apiCount !== undefined && apiCount !== null ? apiCount : (talentPoolsStore.members?.length || 0);
});

const formattedAverageExperience = computed(() => formatMonths(activePool.value?.average_experience_months));
const formattedAverageTenure = computed(() => formatMonths(activePool.value?.average_tenure_months));

const averageAvailabilityDays = computed(() => {
  const days = activePool.value?.average_availability_days;
  if (days === undefined || days === null) return null;
  return Math.max(0, Math.round(days));
});

const availabilityDisplay = computed(() => {
  const days = averageAvailabilityDays.value;
  if (days === null) return '—';
  if (days <= 0) return 'Now';
  if (days === 1) return '1 day';
  if (days < 14) return `${days} days`;
  const weeks = Math.round(days / 7);
  return `${weeks} wk${weeks === 1 ? '' : 's'}`;
});

const availableNowCount = computed(() => {
  const members = talentPoolsStore.members || [];
  if (!members.length) return null;
  return members.reduce((acc, member) => {
    const days = availabilityToDays(member.availability);
    return days !== null && days <= 0 ? acc + 1 : acc;
  }, 0);
});

const statCards = computed(() => ([
  {
    label: 'Members',
    value: membersCount.value.toLocaleString(),
    icon: Users,
    iconBg: 'bg-teal-50',
    iconRing: 'ring-teal-100',
    iconColor: 'text-teal-600',
    subtext: 'In this pool'
  },
  {
    label: 'Availability',
    value: availabilityDisplay.value,
    icon: Timer,
    iconBg: 'bg-indigo-50',
    iconRing: 'ring-indigo-100',
    iconColor: 'text-indigo-600',
    subtext: 'Typical start lead'
  },
  {
    label: 'Available now',
    value: availableNowCount.value !== null ? availableNowCount.value.toLocaleString() : '—',
    icon: CheckCircle,
    iconBg: 'bg-emerald-50',
    iconRing: 'ring-emerald-100',
    iconColor: 'text-emerald-600',
    subtext: 'Can start immediately'
  },
  {
    label: 'Avg Tenure',
    value: formattedAverageTenure.value,
    icon: Briefcase,
    iconBg: 'bg-orange-50',
    iconRing: 'ring-orange-100',
    iconColor: 'text-orange-600',
    subtext: 'Avg stay per employer'
  },
  {
    label: 'Avg Experience',
    value: formattedAverageExperience.value,
    icon: Award,
    iconBg: 'bg-purple-50',
    iconRing: 'ring-purple-100',
    iconColor: 'text-purple-600',
    subtext: 'Across members'
  }
]));

const talentPoolStatus = computed(() => {
  if (!activePool.value) return {};
  switch (activePool.value.status) {
    case 'active':
      return { text: 'Active', classes: 'bg-green-100 text-green-800' };
    case 'paused':
      return { text: 'Paused', classes: 'bg-amber-100 text-amber-800' };
    default:
      return { text: 'Closed', classes: 'bg-gray-100 text-gray-800' };
  }
});

const filteredContractors = computed(() => {
  if (!activePool.value || !talentPoolsStore.members) return [];
  const query = searchQuery.value.toLowerCase();

  const now = new Date();
  const daysBetween = (dateString) => {
    const parsed = dateString ? new Date(dateString) : null;
    if (!parsed || isNaN(parsed.getTime())) return null;
    const diffMs = now - parsed;
    return diffMs >= 0 ? diffMs / (1000 * 60 * 60 * 24) : null;
  };

  const availabilityThreshold = (selected) => {
    switch (selected) {
      case 'immediate':
        return 0;
      case 'one-week':
        return 7;
      case 'two-weeks':
        return 14;
      case 'one-month':
        return 35;
      default:
        return null;
    }
  };

  const joinRangeDays = (selected) => {
    switch (selected) {
      case 'this-week':
        return { max: 7 };
      case 'two-weeks':
        return { max: 14 };
      case 'one-month':
        return { max: 30 };
      case 'three-months':
        return { max: 90 };
      case 'older':
        return { min: 90 };
      default:
        return null;
    }
  };

  return talentPoolsStore.members.filter(c => {
    const fullName = (c.full_name || '').toLowerCase();
    const role = (c.current_role || '').toLowerCase();
    const matchesQuery = !query
      || fullName.includes(query)
      || role.includes(query);

    const rateValue = c.rate ? parseFloat(c.rate) : null;
    const matchesMinRate = filters.minRate === '' || (rateValue !== null && !isNaN(rateValue) && rateValue >= parseFloat(filters.minRate));
    const matchesMaxRate = filters.maxRate === '' || (rateValue !== null && !isNaN(rateValue) && rateValue <= parseFloat(filters.maxRate));

    const availabilityDays = availabilityToDays(c.availability);
    const selectedAvail = availabilityThreshold(filters.availability);
    const matchesAvailability =
      !filters.availability
        ? true
        : (availabilityDays !== null && selectedAvail !== null && availabilityDays <= selectedAvail) ||
          (filters.availability === 'not-specified' && (c.availability || '').toLowerCase().includes('not specified'));

    const joinedDays = daysBetween(c.application_date);
    const joinWindow = joinRangeDays(filters.joinedRange);
    const matchesJoin = !joinWindow
      ? true
      : joinWindow.min !== undefined && joinWindow.max !== undefined
        ? joinedDays !== null && joinedDays >= joinWindow.min && joinedDays <= joinWindow.max
        : joinWindow.min !== undefined
          ? joinedDays !== null && joinedDays >= joinWindow.min
          : joinWindow.max !== undefined
            ? joinedDays !== null && joinedDays <= joinWindow.max
            : true;

    return matchesQuery && matchesMinRate && matchesMaxRate && matchesAvailability && matchesJoin;
  });
});

const contractorsWithCoords = computed(() =>
  filteredContractors.value
    .map((contractor, index) => {
      const contractorId = getContractorId(contractor) || `contractor-${index}`;
      const lat = parseCoordinate(contractor?.location_lat);
      const lng = parseCoordinate(contractor?.location_lng);
      if (lat === null || lng === null) return null;
      return {
        ...contractor,
        _computedId: contractorId,
        coordinates: { lat, lng },
        photoUrl: getContractorPhoto(contractor),
      };
    })
    .filter(Boolean)
);

watch(
  contractorsWithCoords,
  (list) => {
    const ids = list.map(getContractorId).filter(Boolean);
    if (!ids.length) {
      selectedContractorId.value = null;
      return;
    }
    if (!ids.includes(selectedContractorId.value)) {
      selectedContractorId.value = ids[0];
    }
  },
  { immediate: true }
);

// --- FUNCTIONS ---
const goBackToList = () => {
  router.push({ name: 'CompanyTalentPoolList' }); // Make sure this route name matches your router config
};

const handleMarkerClick = (contractor) => {
  const contractorId = getContractorId(contractor);
  if (!contractorId) return;
  selectedContractorId.value = contractorId;
  scrollCardIntoView(contractorId);
};

const handleSelectContractor = (contractor) => {
  const contractorId = getContractorId(contractor);
  if (!contractorId) return;
  if (!contractorHasCoordinates(contractor)) {
    selectedContractorId.value = null; // Clear selection to close any open popup
    return;
  }
  selectedContractorId.value = contractorId;
};

const scrollCardIntoView = (contractorId) => {
  nextTick(() => {
    const target = document.getElementById(`contractor-card-${contractorId}`);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });
};

const handleViewProfile = (contractor) => {
  const profileHash = contractor?.profile_hash;
  const poolId = activePool.value?.id;
  if (!profileHash || !poolId) return;
  router.push({ name: 'CompanyTalentPoolCandidateView', params: { poolId, profile_hash: profileHash } });
};

const handleDownloadResume = (contractorId) => {
  talentPoolsStore.getMemberResume(poolId, contractorId)
}

const handleMessage = (contractorId) => console.log('Message:', contractorId);
const resetFilters = () => {
  filters.minRate = '';
  filters.maxRate = '';
  filters.availability = '';
  filters.joinedRange = '';
};
</script>
